// src/components/MeetingForm.js
import React, { useState, forwardRef, useImperativeHandle, useMemo, useEffect, useCallback, useRef } from 'react';
import { generateId } from '../services/utils'; 
import AgendaItem from './agenda/AgendaItem';
import AgendaProposalModal from './agenda/AgendaProposalModal';
import TranslationJobModal from './agenda/TranslationJobModal'; 
import GuestAssignmentModal from './agenda/GuestAssignmentModal'; 

// === Firebase Imports ===
import { 
    collection, query, where, onSnapshot, 
    addDoc, updateDoc, doc, serverTimestamp, writeBatch, getDoc 
} from 'firebase/firestore';
import { db } from '../services/firebase'; 

// --- Icons ---
const IconSidebarToggle = ({ isOpen }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-0' : 'rotate-180'}`}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5" />
    </svg>
);

// --- Lokale Helper Funktion: Sicheres Nummerieren & Initialisieren ---
const safeReindexAgendaItems = (items, parentNumber = '') => {
    if (!Array.isArray(items)) return [];
    return items.map((item, index) => {
        const number = parentNumber ? `${parentNumber}${index + 1}.` : `${index + 1}.`;
        return {
            ...item, 
            number,
            guests: Array.isArray(item.guests) ? item.guests : [], 
            guestUids: Array.isArray(item.guestUids) ? item.guestUids : [],
            subPoints: safeReindexAgendaItems(item.subPoints || [], number)
        };
    });
};

// --- Helper Component: Accordion Section ---
const AccordionSection = ({ title, children, isOpen, onToggle, icon }) => {
    return (
        <div className="border-b border-gray-200 dark:border-gray-700 last:border-0 flex-shrink-0">
            <button 
                onClick={onToggle}
                className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
            >
                <div className="flex items-center gap-2 font-bold text-sm text-gray-700 dark:text-gray-300">
                    {icon}
                    <span>{title}</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
            </button>
            {isOpen && (
                <div className="p-3 bg-white dark:bg-gray-900/30 animate-fadeIn text-sm">
                    {children}
                </div>
            )}
        </div>
    );
};

// --- Helper Component: Projekt Auswahl ---
const ProjectAutocomplete = ({ companyId, userUid, onSelect, initialValue, isDisabled, label, onNameResolved }) => {
    const [projects, setProjects] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    const selectedIdRef = useRef(initialValue || '');
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!userUid || !companyId) {
            setIsLoading(false);
            return; 
        }
        const q = query(
            collection(db, "companies", companyId, "projects"),
            where("members", "array-contains", userUid)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loaded = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            loaded.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setProjects(loaded);
            setIsLoading(false);
        }, (error) => setIsLoading(false));
        return () => unsubscribe();
    }, [userUid, companyId]);

    useEffect(() => {
        if (!initialValue) {
            if (inputValue !== '') { setInputValue(''); selectedIdRef.current = ''; }
            return;
        }
        const currentProject = projects.find(p => p.id === initialValue);
        if (currentProject) {
            if (inputValue !== currentProject.name) {
                setInputValue(currentProject.name);
                selectedIdRef.current = initialValue;
                if (onNameResolved) onNameResolved(currentProject.name);
            }
        } else if (companyId && initialValue) {
            if (!inputValue || selectedIdRef.current !== initialValue) {
                const docRef = doc(db, "companies", companyId, "projects", initialValue);
                getDoc(docRef).then(snap => {
                     if (snap.exists()) {
                         const name = snap.data().name;
                         setInputValue(name);
                         selectedIdRef.current = initialValue;
                         if (onNameResolved) onNameResolved(name);
                     }
                 }).catch(e => console.error("Fehler beim Einzelabruf:", e.message));
            }
        }
    }, [initialValue, projects, companyId]); 

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const commitSelection = (id, name) => {
        selectedIdRef.current = id;
        onSelect({ id, name, isManual: false });
        if (onNameResolved) onNameResolved(name);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        setShowSuggestions(true);
        if (val === '') { commitSelection('', ''); return; }
    };

    const handleSelectProject = (project) => {
        setInputValue(project.name);
        setShowSuggestions(false);
        commitSelection(project.id, project.name);
    };

    const handleBlur = () => {
        setTimeout(() => {
            if (selectedIdRef.current) {
                if (inputValue.trim() === '') commitSelection('', '');
            } else { setInputValue(''); }
            setShowSuggestions(false);
        }, 200);
    };

    const filteredProjects = projects.filter(p => (p.name || '').toLowerCase().includes(inputValue.toLowerCase()));

    return (
        <div className="relative" ref={wrapperRef}>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{label || "Projekt"}</label>
            <div className="relative">
                <input 
                    type="text" 
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onFocus={() => !isDisabled && setShowSuggestions(true)}
                    placeholder={isLoading ? "Lade..." : "Suchen..."}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:bg-gray-100 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    disabled={isDisabled}
                    autoComplete="off"
                />
                {isLoading && (
                    <div className="absolute right-2 top-2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                    </div>
                )}
            </div>
            {showSuggestions && !isDisabled && (
                <ul className="absolute z-30 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-xl">
                    {filteredProjects.length > 0 ? (
                        filteredProjects.map(p => (
                            <li key={p.id} onMouseDown={() => handleSelectProject(p)} className="px-3 py-2 hover:bg-indigo-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0">
                                <div className="font-medium text-gray-800 dark:text-white text-sm">{p.name}</div>
                            </li>
                        ))
                    ) : (
                        <li className="px-3 py-2 text-gray-400 text-sm italic">Kein Projekt gefunden.</li>
                    )}
                </ul>
            )}
        </div>
    );
};

// --- Helper Component: Teilnehmer Auswahl ---
const ParticipantAutocomplete = ({ users, teams = [], onSelect, placeholder, existingParticipants, inputId, isDisabled }) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    
    const filteredUsers = useMemo(() => users.filter(user => !existingParticipants.some(p => p.uid === user.uid)), [users, existingParticipants]);
    const filteredTeams = useMemo(() => teams || [], [teams]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        if (value) {
            const userSuggestions = filteredUsers.filter(user => {
                const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                return displayName.toLowerCase().includes(value.toLowerCase()) || (user.email || '').toLowerCase().includes(value.toLowerCase());
            }).map(user => ({ ...user, displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email })); 
            
            const teamSuggestions = filteredTeams.filter(team => (team.name || '').toLowerCase().includes(value.toLowerCase())).map(team => ({ ...team, isTeam: true, displayName: `${team.name} (Team)` }));
            
            const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            
            let newSuggestions = [...userSuggestions, ...teamSuggestions];
            if (isEmail && !newSuggestions.some(s => s.email === value)) {
                newSuggestions.push({ isGuestSuggestion: true, email: value.toLowerCase(), name: `Gast hinzufügen: ${value.toLowerCase()}`, displayName: `Gast: ${value.toLowerCase()}` });
            }
            setSuggestions(newSuggestions);
            setShowSuggestions(true);
        } else {
            const allUserSuggestions = filteredUsers.map(user => ({ ...user, displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email }));
            const allTeamSuggestions = filteredTeams.map(team => ({ ...team, isTeam: true, displayName: `${team.name} (Team)` }));
            setSuggestions([...allUserSuggestions, ...allTeamSuggestions]);
            setShowSuggestions(true);
        }
    };

    const handleSelect = (selection) => { 
        onSelect(selection); 
        setInputValue(''); 
        setShowSuggestions(false); 
    };

    return (
        <div className="relative flex-grow">
            <input type="text" value={inputValue} onChange={handleInputChange} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} onFocus={() => { if (!isDisabled) setShowSuggestions(true); }} placeholder={isDisabled ? "Nur Moderator..." : placeholder} className={`w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm transition-colors duration-200 shadow-sm ${isDisabled ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''}`} id={inputId || "participant-input"} name={inputId || "participantInput"} disabled={isDisabled} />
            {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute z-20 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                    {suggestions.map((item, index) => (
                        <li key={item.uid || item.id || item.email || index} onMouseDown={() => handleSelect(item)} className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex items-center text-sm">
                           {item.isGuestSuggestion ? '📧' : item.isTeam ? '👥' : '👤'} <span className="ml-2">{item.displayName || item.name}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// --- Hauptkomponente: MeetingForm ---
const MeetingForm = forwardRef(({ 
  meeting, onSave, onCancel, setNotification, 
  currentUserId, currentCompanyId, companyUsers, teams, 
  allTasks, onSendInvites, onSyncAttendees, onUpdateParticipantStatus,
  agendaProposals = [], onProposeAgendaItem, onReviewAgendaItem,
  onCreateTranslationJob, onCancelTranslationJob
}, ref) => {
  const today = new Date().toISOString().split('T')[0];

  const [currentMeetingId, setCurrentMeetingId] = useState(meeting?.id || generateId());
  
  // === RIGHT SIDEBAR STATE ===
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [openSections, setOpenSections] = useState({
      details: true,
      participants: true,
      resources: true
  });
  const toggleSection = (section) => setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));

  useEffect(() => {
      const handleResize = () => {
          if (window.innerWidth < 1024) setRightSidebarOpen(false);
      };
      if (window.innerWidth < 1024) setRightSidebarOpen(false);
  }, []);

  useEffect(() => {
    if (meeting?.id) {
        setCurrentMeetingId(meeting.id);
    }
  }, [meeting?.id]);

  // State Variables
  const [internalMeeting, setInternalMeeting] = useState(meeting);
  const [title, setTitle] = useState(internalMeeting ? internalMeeting.title : '');
  const [date, setDate] = useState(internalMeeting ? internalMeeting.date : today);
  const [startTime, setStartTime] = useState(internalMeeting ? internalMeeting.startTime : new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
  const [endTime, setEndTime] = useState(internalMeeting ? internalMeeting.endTime : '');
  const [location, setLocation] = useState(internalMeeting ? internalMeeting.location : '');
  
  const [projectId, setProjectId] = useState(internalMeeting ? internalMeeting.projectId : '');
  const [topicSourceName, setTopicSourceName] = useState('');

  const [visibility, setVisibility] = useState(internalMeeting ? internalMeeting.visibility : 'internal');
  const [allParticipants, setAllParticipants] = useState(internalMeeting?.allParticipants || []);
  const [agenda, setAgenda] = useState(internalMeeting ? internalMeeting.agenda : []);
  const [status, setStatus] = useState(internalMeeting?.status || 'forecast'); 
  const [moderatorUid, setModeratorUid] = useState(internalMeeting?.moderatorUid || internalMeeting?.creatorUserId || currentUserId);
  const [isSendInvitesButtonVisible, setIsSendInvitesButtonVisible] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  
  // === GUEST MODAL STATES ===
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [currentEditingItemId, setCurrentEditingItemId] = useState(null);
  const [currentGuestsForModal, setCurrentGuestsForModal] = useState([]);

  const [targetLangForModal, setTargetLangForModal] = useState(null);
  const [selectedTransLang, setSelectedTransLang] = useState('en'); 

  // === STATE FÜR THEMENSPEICHER ===
  const [serverTopics, setServerTopics] = useState([]);
  const [hiddenTopicIds, setHiddenTopicIds] = useState([]); 
  
  // === DRAG & DROP STATE ===
  const [dragOverIndex, setDragOverIndex] = useState(null); 
  const [isDragOverPool, setIsDragOverPool] = useState(false); 

  const isModerator = useMemo(() => currentUserId === moderatorUid, [currentUserId, moderatorUid]);

  const handleOnlineToggle = (e) => {
      if (e.target.checked) setLocation('Online-Meeting');
      else setLocation('');
  };
  const isOnlineMeeting = location === 'Online-Meeting' || (location || '').toLowerCase().includes('online');

  // === HYDRATION FIX & PROJECT LOADING ===
  useEffect(() => {
    const hydrateMeetingData = async () => {
        if (meeting?.id && currentCompanyId) {
            try {
                const docRef = doc(db, 'companies', currentCompanyId, 'meetings', meeting.id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.projectId) setProjectId(data.projectId);
                } else {
                    // WICHTIG: Wenn das Dokument nicht existiert (z.B. Forecast/Entwurf), 
                    // nicht versuchen weitere Daten zu laden, um "Master Dokument nicht gefunden" zu vermeiden.
                    console.warn("Meeting Dokument existiert noch nicht in DB (könnte neu oder Forecast sein).");
                }
            } catch (e) {
                console.log("Hydration error:", e.message);
            }
        }
    };
    hydrateMeetingData();
  }, [meeting?.id, currentCompanyId]);


  // === DATA LOADING (THEMENSPEICHER) ===
  useEffect(() => {
      if (!projectId || projectId.length <= 2 || !currentCompanyId) {
          setServerTopics([]);
          return;
      }
      const q = query(
          collection(db, "companies", currentCompanyId, "projectTopics"),
          where("projectId", "==", projectId),
          where("status", "==", "open")
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
          const loaded = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : (data.createdAt || 0)
            };
          });
          loaded.sort((a, b) => b.createdAt - a.createdAt);
          setServerTopics(loaded);
          setHiddenTopicIds(prev => prev.filter(id => loaded.some(t => t.id === id)));
      }, (error) => {
          console.error("Fehler beim Laden der Themen:", error);
      });
      return () => unsubscribe();
  }, [projectId, currentCompanyId]);

  const displayedPool = useMemo(() => {
      return serverTopics.filter(t => !hiddenTopicIds.includes(t.id));
  }, [serverTopics, hiddenTopicIds]);

  const handleSubmit = async (e, goToListAfterSave = false) => {
    if(e && e.preventDefault) e.preventDefault();
    if (!title || title.trim() === '') { setNotification({ message: 'Bitte geben Sie dem Protokoll einen Titel.', type: 'error' }); return; }
    if (!currentUserId || !currentCompanyId) { setNotification({ message: 'Benutzer oder Firma nicht authentifiziert.', type: 'error' }); return; }
    
    // Safety check before saving: Ensure agenda structure is robust
    const safeAgenda = safeReindexAgendaItems(agenda);

    const meetingData = {
      id: currentMeetingId,
      title, date, startTime, endTime, location, allParticipants, 
      agenda: safeAgenda, // Use clean agenda
      status,
      projectId: projectId || null,
      topicSourceId: null, 
      visibility: visibility,
      participantUids: allParticipants.filter(p => p.uid).map(p => p.uid),
      moderatorUid: moderatorUid, creatorUserId: internalMeeting?.creatorUserId || currentUserId,
      translationJobs: internalMeeting?.translationJobs || {},
      updatedAt: new Date()
    };

    try { 
        await onSave(meetingData, goToListAfterSave); 

        const topicsToUpdate = safeAgenda.filter(item => item.sourceTopicId);
        if (topicsToUpdate.length > 0) {
            const batch = writeBatch(db);
            topicsToUpdate.forEach(item => {
                const ref = doc(db, "companies", currentCompanyId, "projectTopics", item.sourceTopicId);
                batch.update(ref, {
                    assignedMeetingTitle: title,
                    assignedMeetingDate: date
                });
            });
            batch.commit().catch(e => console.error("Batch update topics failed", e));
        }
    } catch (error) { 
        console.error('Fehler beim Speichern des Protokolls:', error); 
        setNotification({ message: `Fehler beim Speichern: ${error.message}`, type: 'error' }); 
    }
  };

  useImperativeHandle(ref, () => ({
    saveCurrentState(shouldGoToList = false) { handleSubmit({ preventDefault: () => {} }, shouldGoToList); }
  }), [handleSubmit]); 

  // *** FIX: Aktualisierung bei Sprachwechsel oder externen Updates ***
  useEffect(() => {
    if (meeting && (
        meeting.id !== internalMeeting?.id || 
        // Wir prüfen, ob sich der Inhalt signifikant geändert hat (z.B. durch Sprachwechsel)
        // Dies verhindert, dass jeder kleine Render das Formular zurücksetzt, 
        // erlaubt aber Updates, wenn das "meeting"-Prop komplett ausgetauscht wurde.
        (meeting.title !== internalMeeting?.title && meeting.title) || 
        (meeting.updatedAt && meeting.updatedAt !== internalMeeting?.updatedAt)
    )) {
        setInternalMeeting(meeting);
        setTitle(meeting.title || '');
        setDate(meeting.date || today);
        setStartTime(meeting.startTime || '');
        setEndTime(meeting.endTime || '');
        setLocation(meeting.location || '');
        setProjectId(meeting.projectId || '');
        setVisibility(meeting.visibility || 'internal');
        setAllParticipants(meeting.allParticipants || []);
        setAgenda(safeReindexAgendaItems(meeting.agenda || []));
        setStatus(meeting.status || 'forecast');
        setModeratorUid(meeting.moderatorUid || meeting.creatorUserId || currentUserId);
    }
  }, [meeting, internalMeeting, currentUserId, today]);
  
  useEffect(() => {
    const hasExternalGuests = allParticipants.some(p => p.isExternal && p.email);
    setIsSendInvitesButtonVisible(hasExternalGuests);
  }, [allParticipants]);

  const internalParticipants = useMemo(() => allParticipants.filter(p => p.uid && !p.isExternal), [allParticipants]);

  useEffect(() => {
      const moderatorIsParticipant = internalParticipants.some(p => p.uid === moderatorUid);
      if (!moderatorIsParticipant && internalParticipants.length > 0) setModeratorUid(internalParticipants[0].uid);
      else if (!moderatorIsParticipant && internalParticipants.length === 0) setModeratorUid(internalMeeting?.creatorUserId || currentUserId);
  }, [internalParticipants, moderatorUid, internalMeeting?.creatorUserId, currentUserId]);


  // === DRAG & DROP LOGIK ===
  const handleDragStart = (e, type, index, itemData = null) => {
      const dragData = JSON.stringify({ type, index, itemData });
      e.dataTransfer.setData('application/meetform-dnd', dragData);
      e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverAgenda = (e, targetIndex) => {
    e.preventDefault(); 
    setDragOverIndex(targetIndex);
    setIsDragOverPool(false);
  };

  const handleDropOnAgenda = async (e, dropIndex) => {
      e.preventDefault();
      setDragOverIndex(null);

      const rawData = e.dataTransfer.getData('application/meetform-dnd');
      if (!rawData) return;

      try {
          const { type, index: sourceIndex, itemData } = JSON.parse(rawData);

          if (type === 'agenda') {
              if (sourceIndex === dropIndex) return;
              let _agenda = [...agenda];
              const [movedItem] = _agenda.splice(sourceIndex, 1);
              _agenda.splice(dropIndex, 0, movedItem);
              setAgenda(safeReindexAgendaItems(_agenda));
          } 
          else if (type === 'pool') {
              const proposal = itemData; 
              if (!proposal) return;

              const agendaTitle = proposal.title || proposal.description;
              const agendaNotes = proposal.title ? proposal.description : (proposal.notes || '');

              const newItem = {
                id: generateId(),
                number: '',
                description: agendaTitle, 
                notes: agendaNotes,       
                tasks: [],
                subPoints: [],
                createdBy: currentUserId,
                sourceTopicId: proposal.id,
                guests: [], // Default empty guests
                guestUids: [] 
              };

              let _agenda = [...agenda];
              _agenda.splice(dropIndex, 0, newItem);
              setAgenda(safeReindexAgendaItems(_agenda));

              setHiddenTopicIds(prev => [...prev, proposal.id]);
              setNotification({ message: 'Punkt übernommen.', type: 'success' });

              try {
                  const topicRef = doc(db, "companies", currentCompanyId, "projectTopics", proposal.id);
                  await updateDoc(topicRef, { 
                      status: 'assigned',
                      assignedMeetingId: currentMeetingId,
                      assignedMeetingTitle: title || 'Unbenanntes Protokoll',
                      assignedMeetingDate: date
                  });
              } catch (error) {
                  console.error("Fehler beim Status-Update:", error);
                  setNotification({ message: 'Fehler beim DB-Update.', type: 'error' });
                  setHiddenTopicIds(prev => prev.filter(id => id !== proposal.id));
              }
          }
      } catch (err) {
          console.error("Drop Parse Error", err);
      }
  };

  const handleDragOverPool = (e) => {
      e.preventDefault();
      setIsDragOverPool(true);
  };

  const handleDropOnPool = async (e) => {
      e.preventDefault();
      setIsDragOverPool(false);
      const rawData = e.dataTransfer.getData('application/meetform-dnd');
      if (!rawData) return;

      try {
          const { type, index: sourceIndex } = JSON.parse(rawData);

          if (type === 'agenda') {
              const itemToMove = agenda[sourceIndex];
              if (!itemToMove) return;

              if (!itemToMove.sourceTopicId) {
                  setNotification({ message: 'Dieser Punkt stammt nicht aus dem Themenspeicher.', type: 'warning' });
                  return;
              }

              const newAgenda = agenda.filter((_, idx) => idx !== sourceIndex);
              setAgenda(safeReindexAgendaItems(newAgenda));

              if (projectId) {
                  try {
                      if (itemToMove.sourceTopicId) {
                          const topicRef = doc(db, "companies", currentCompanyId, "projectTopics", itemToMove.sourceTopicId);
                          await updateDoc(topicRef, {
                              status: 'open',
                              assignedMeetingId: null,
                              assignedMeetingTitle: null,
                              assignedMeetingDate: null
                          });
                          setNotification({ message: 'Thema zurück in den Speicher geschoben.', type: 'success' });
                      } 
                  } catch (error) {
                      console.error("Fehler beim Zurückschieben:", error);
                      setNotification({ message: 'Konnte DB nicht erreichen.', type: 'error' });
                  }
              } else {
                  setNotification({ message: 'Kein Projekt verknüpft.', type: 'warning' }); 
              }
          }
      } catch (err) {
          console.error("Pool Drop Error", err);
      }
  };

  const handleOpenTranslateModal = (targetLang) => {
      const langToUse = targetLang || selectedTransLang;
      setTargetLangForModal(langToUse);
      setShowTranslateModal(true);
  };

  // --- HANDLER FÜR TEILNEHMER ---
  const handleAddParticipant = (selection) => {
    if (selection.isTeam) {
        const team = teams.find(t => t.id === selection.id);
        if (!team || !Array.isArray(team.members)) return;
        const newParticipantsToAdd = [];
        team.members.forEach(memberUid => {
            if (!allParticipants.some(p => p.uid === memberUid)) {
                const user = companyUsers.find(u => u.uid === memberUid);
                if (user) newParticipantsToAdd.push({ id: user.uid, uid: user.uid, name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email, email: user.email, isExternal: false, currentStatus: 'pending', events: [] });
            } 
        });
        if (newParticipantsToAdd.length > 0) {
            setAllParticipants(prev => [...prev, ...newParticipantsToAdd]);
            setNotification({ message: `${newParticipantsToAdd.length} Mitglied(er) von Team "${team.name}" hinzugefügt.`, type: 'success' });
        }
    } else if (selection.isGuestSuggestion) { 
        setAllParticipants(prev => [...prev, { id: generateId(), name: `Gast (${selection.email})`, email: selection.email, isExternal: true, currentStatus: 'pending', events: [] }]);
    } else if (selection.uid) {
        if (allParticipants.some(p => p.uid === selection.uid)) { setNotification({ message: 'Dieser Benutzer ist bereits auf der Liste.', type: 'info' }); return; }
        setAllParticipants(prev => [...prev, { id: selection.uid, uid: selection.uid, name: `${selection.firstName} ${selection.lastName}`, email: selection.email, isExternal: false, currentStatus: 'pending', events: [] }]);
    }
  };
  const handleRemovePerson = (idToRemove) => { setAllParticipants(prev => prev.filter(p => p.id !== idToRemove)); };
  const handleUpdateParticipantStatusLocal = (id, newStatus, isSelf) => {
    if (isSelf) { onUpdateParticipantStatus(internalMeeting.id, id, newStatus); return; }
    const timestamp = new Date().toISOString();
    let event = null;
    
    if (newStatus === 'absent') event = { type: 'leave', timestamp: timestamp };
    else if (newStatus === 'present') event = { type: 'join', timestamp: timestamp };
    
    setAllParticipants(prev => prev.map(p => {
        if (p.id === id) return { ...p, currentStatus: newStatus, events: event ? [...(p.events || []), event] : p.events };
        return p;
    }));
  };
  const handleSetTimeEvent = (participantId, type) => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const timeStr = `${hours}:${minutes}`;
      const dateTimeStr = `${date}T${timeStr}`;
      const eventDate = new Date(dateTimeStr);
      if (isNaN(eventDate.getTime())) { setNotification({message: "Zeitfehler.", type: "error"}); return; }
      const timestamp = eventDate.toISOString();
      const event = { type: type === 'late' ? 'join' : 'leave', timestamp: timestamp, manual: true, label: timeStr };
      const statusLabel = type === 'late' ? 'joined_late' : 'left_early';
      
      setAllParticipants(prev => prev.map(p => {
          if (p.id === participantId) return { ...p, currentStatus: statusLabel, events: [...(p.events || []), event] };
          return p;
      }));
  };
  const handleClearManualEvents = (participantId) => {
      setAllParticipants(prev => prev.map(p => {
          if (p.id === participantId) {
             const cleanEvents = (p.events || []).filter(e => !e.manual);
             return { ...p, currentStatus: 'present', events: cleanEvents };
          }
          return p;
      }));
  }

  // --- HANDLER FÜR AGENDA ---
  const handleAddAgendaItem = useCallback(() => {
    const newAgendaItem = { id: generateId(), number: '', description: 'Neuer Agendapunkt', notes: '', tasks: [], subPoints: [], createdBy: currentUserId, guests: [], guestUids: [] };
    const updatedAgenda = safeReindexAgendaItems([...(agenda || []), newAgendaItem]); 
    setAgenda(updatedAgenda);
  }, [agenda, currentUserId]);
  
  const handleUpdateAgendaItem = useCallback((itemId, updatedItem) => {
    const recursiveUpdate = (items) => (items || []).map(item => {
        if (item.id === itemId) return updatedItem;
        if (Array.isArray(item.subPoints) && item.subPoints.length > 0) return { ...item, subPoints: recursiveUpdate(item.subPoints) };
        return item;
    });
    setAgenda(safeReindexAgendaItems(recursiveUpdate(agenda)));
  }, [agenda]); 

  // --- GUEST ASSIGNMENT HANDLERS ---
  const handleAssignGuest = (itemId) => {
      const findItem = (items) => {
          for (const item of items) {
              if (item.id === itemId) return item;
              if (item.subPoints) {
                  const found = findItem(item.subPoints);
                  if (found) return found;
              }
          }
          return null;
      };
      
      const item = findItem(agenda);
      if (item) {
          setCurrentEditingItemId(itemId);
          setCurrentGuestsForModal(item.guests || []);
          setShowGuestModal(true);
      }
  };

  const handleSaveGuests = (selectedGuests) => {
      if (!currentEditingItemId) return;
      
      const guestUids = selectedGuests.map(g => g.uid).filter(uid => uid);
      
      const recursiveUpdate = (items) => (items || []).map(item => {
          if (item.id === currentEditingItemId) {
              return { ...item, guests: selectedGuests, guestUids: guestUids };
          }
          if (Array.isArray(item.subPoints) && item.subPoints.length > 0) {
              return { ...item, subPoints: recursiveUpdate(item.subPoints) };
          }
          return item;
      });
      
      setAgenda(safeReindexAgendaItems(recursiveUpdate(agenda)));
      setCurrentEditingItemId(null);
      setNotification({ message: 'Gäste für diesen Punkt gespeichert.', type: 'success' });
  };

  const handleAddSubPoint = useCallback((parentId) => {
    const recursiveAdd = (items) => (items || []).map(item => {
        if (item.id === parentId) {
            const newSubPoint = { id: generateId(), number: '', description: 'Neuer Unterpunkt', notes: '', tasks: [], subPoints: [], createdBy: currentUserId, guests: [], guestUids: [] };
            return { ...item, subPoints: safeReindexAgendaItems([...(item.subPoints || []), newSubPoint], item.number) };
        }
        if (Array.isArray(item.subPoints) && item.subPoints.length > 0) return { ...item, subPoints: recursiveAdd(item.subPoints) };
        return item;
    });
    setAgenda(safeReindexAgendaItems(recursiveAdd(agenda)));
  }, [agenda, currentUserId]);

  const handleDeleteAgendaItem = useCallback((itemId, isTask = false) => {
    const recursiveDelete = (items, itemIdToDelete, isTaskDeletion) => (items || []).map(item => {
            if (isTaskDeletion) {
                const tasksFiltered = (item.tasks || []).filter(task => task.id !== itemIdToDelete);
                return { ...item, tasks: tasksFiltered, subPoints: recursiveDelete(item.subPoints, itemIdToDelete, true) };
            }
            if (item.id === itemIdToDelete) return null;
            return { ...item, subPoints: recursiveDelete(item.subPoints, itemIdToDelete, isTaskDeletion) };
        }).filter(item => item !== null);
    setAgenda((prevAgenda) => safeReindexAgendaItems(recursiveDelete(prevAgenda, itemId, isTask)));
  }, []); 
  
  const handleSendClick = async (e) => { e.preventDefault(); if (internalMeeting?.id && internalMeeting?.title) await onSendInvites(internalMeeting, 48); else setNotification({ message: 'Bitte speichern Sie das Protokoll zuerst.', type: 'warning' }); };
  const handleProposeClick = () => setShowProposalModal(true);
  
  // *** FIX: Wir nutzen hier 'currentMeetingId' statt 'internalMeeting?.id'
  const handleProposalSubmit = (description) => { 
      if (onProposeAgendaItem) {
          onProposeAgendaItem(description, currentMeetingId, currentCompanyId); 
          setShowProposalModal(false); 
      }
  };
  
  const handleTranslateSubmit = (targetLanguage, translatorEmail) => {
    const idToUse = currentMeetingId || internalMeeting?.id;
    
    if (!idToUse || !targetLangForModal) { setNotification({ message: 'Bitte speichern Sie das Protokoll zuerst.', type: 'error' }); return; }
    if (onCreateTranslationJob) {
      onCreateTranslationJob(idToUse, targetLangForModal, translatorEmail);
      setInternalMeeting(prev => ({ ...prev, translationJobs: { ...(prev?.translationJobs || {}), [targetLangForModal]: 'pending' } }));
    }
    setShowTranslateModal(false); setTargetLangForModal(null); 
  };
  const handleCancelClick = async (targetLang) => {
      const idToUse = currentMeetingId || internalMeeting?.id;
      if (window.confirm(`Auftrag für ${targetLang.toUpperCase()} stornieren?`) && onCancelTranslationJob && idToUse) {
          try { await onCancelTranslationJob(idToUse, targetLang); setInternalMeeting(prev => { const j = { ...prev.translationJobs }; delete j[targetLang]; return { ...prev, translationJobs: j }; }); } catch (e) { setNotification({ message: 'Fehler beim Stornieren.', type: 'error' }); }
      }
  };

  const presentParticipants = allParticipants.filter(p => p.currentStatus === 'present' || p.currentStatus === 'joined_late' || p.currentStatus === 'left_early');
  const absentParticipants = allParticipants.filter(p => p.currentStatus === 'absent');
  const pendingParticipants = allParticipants.filter(p => p.currentStatus === 'pending');
  const distributionParticipants = allParticipants.filter(p => p.currentStatus === 'distribution');
  
  const pendingProposals = useMemo(() => (agendaProposals || []).filter(p => p.status === 'pending'), [agendaProposals]);

  return (
    // HAUPT-CONTAINER (Vollbild Flexbox)
    <div className="flex h-full w-full bg-gray-50 dark:bg-gray-900 overflow-hidden relative">
      
      {/* === MITTLERER BEREICH: AGENDA & TOOLBAR === */}
      <main className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-900 relative">
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8 pb-32 scrollbar-thin">
            <div className="max-w-4xl mx-auto min-h-full bg-white dark:bg-gray-800 shadow-xl rounded-xl border border-gray-100 dark:border-gray-700 relative"> 
                
                {/* --- TOGGLE BUTTON RIGHT SIDEBAR (Top Right in Content) --- */}
                {/* FIX: Z-Index 50 um sicherzustellen, dass er klickbar bleibt */}
                <div className="absolute top-4 right-4 z-50">
                     <button 
                        onClick={() => setRightSidebarOpen(!rightSidebarOpen)} 
                        className={`p-2 rounded-full transition-colors shadow-sm border border-gray-200 dark:border-gray-600 ${
                            rightSidebarOpen 
                            ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' 
                            : 'bg-white text-gray-500 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                        title={rightSidebarOpen ? "Menü schließen" : "Menü öffnen"}
                     >
                         <IconSidebarToggle isOpen={rightSidebarOpen} />
                    </button>
                </div>

                {/* --- OFFENE ANTRÄGE --- */}
                {isModerator && pendingProposals.length > 0 && (
                    <div className="m-6 bg-yellow-50 dark:bg-yellow-900/10 p-4 rounded-xl border border-yellow-200 dark:border-yellow-800 animate-fadeIn">
                        <h3 className="text-sm font-bold uppercase text-yellow-800 dark:text-yellow-200 mb-3">📬 Offene Anträge</h3>
                        <div className="space-y-2">
                            {pendingProposals.map(p => (
                                <div key={p.id} className="flex justify-between items-center bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm border border-yellow-100 dark:border-gray-700">
                                    <span className="font-medium text-sm">{p.description}</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => onReviewAgendaItem(p.id, 'approve')} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 font-bold">Annehmen</button>
                                        <button onClick={() => onReviewAgendaItem(p.id, 'deny')} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Ablehnen</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- AGENDA LISTE --- */}
                <div className="p-6 md:p-10 pt-16">
                    <fieldset disabled={!isModerator} className="disabled:opacity-90">
                        <div className="space-y-6">
                            {agenda.length === 0 && (
                                <div 
                                    className={`border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-16 text-center transition-colors ${isDragOverPool === 'agenda' ? 'bg-blue-50 border-blue-400' : ''}`}
                                    onDragOver={(e) => { e.preventDefault(); setDragOverIndex(0); setIsDragOverPool(false); }}
                                    onDrop={(e) => handleDropOnAgenda(e, 0)}
                                >
                                    <div className="text-5xl mb-4 opacity-30">📝</div>
                                    <p className="text-gray-400 font-medium">Starten Sie hier Ihre Agenda.</p>
                                </div>
                            )}

                            {(agenda || []).map((item, index) => (
                            <div
                                key={item.id}
                                draggable={isModerator} 
                                onDragStart={(e) => handleDragStart(e, 'agenda', index)}
                                onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); setIsDragOverPool(false); }}
                                onDrop={(e) => handleDropOnAgenda(e, index)}
                                className={`transition-all duration-200 ease-in-out ${dragOverIndex === index ? 'border-t-4 border-blue-500 pt-2' : ''}`}
                            >
                                <AgendaItem 
                                    item={{...item, number: (item.number && !item.number.endsWith('.')) ? `${item.number}.` : item.number}} 
                                    level={0} 
                                    onUpdate={handleUpdateAgendaItem} 
                                    onAddSubPoint={handleAddSubPoint} 
                                    onDeleteItem={handleDeleteAgendaItem} 
                                    setNotification={setNotification} 
                                    companyUsers={companyUsers} 
                                    teams={teams} 
                                    meetingId={currentMeetingId} 
                                    currentUserId={currentUserId} 
                                    isModerator={isModerator} 
                                    totalParticipants={allParticipants.length} 
                                    allParticipants={allParticipants} 
                                    companyId={currentCompanyId} 
                                    isExternal={internalMeeting?.isExternal || false}
                                    onProposeAgendaItem={onProposeAgendaItem}
                                    onAssignGuest={handleAssignGuest} 
                                />
                            </div>
                            ))}
                        </div>
                        {isModerator && (
                            <div className="mt-10 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <button type="button" onClick={handleAddAgendaItem} className="group w-full py-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-indigo-300 dark:border-gray-700 dark:hover:border-indigo-700 text-gray-400 hover:text-indigo-600 transition-all flex items-center justify-center gap-2 font-medium">
                                    <span className="group-hover:scale-110 transition-transform text-xl">+</span> Punkt hinzufügen
                                </button>
                            </div>
                        )}
                    </fieldset>
                </div>
            </div>
        </div>

        {/* --- BOTTOM ACTION BAR (STICKY) --- */}
        <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 px-6 flex items-center justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-30">
            {/* ... Buttons ... */}
            <div className="flex items-center gap-3">
                <button onClick={onCancel} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium text-sm transition-colors">Zurück</button>
                {isModerator ? (
                    <>
                    <button onClick={(e) => handleSubmit(e, false)} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-lg font-bold text-sm transition-transform hover:scale-105">Speichern</button>
                    {isSendInvitesButtonVisible && (
                        <button onClick={handleSendClick} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow font-medium text-sm transition-transform hover:scale-105">Einladen</button>
                    )}
                    </>
                ) : (
                    <button onClick={handleProposeClick} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg font-bold text-sm">Vorschlag</button>
                )}
            </div>
            <div className="flex items-center gap-4">
                 {isModerator && (
                   <label className="flex items-center gap-2 cursor-pointer bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full border border-transparent hover:border-gray-300 transition-colors">
                        <span className={`text-xs font-bold uppercase ${status === 'forecast' ? 'text-yellow-600' : 'text-gray-500'}`}>Plan</span>
                        <div className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={status === 'completed'} onChange={(e) => setStatus(e.target.checked ? 'completed' : 'forecast')} className="sr-only peer" />
                            <div className={`w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500`}></div>
                        </div>
                        <span className={`text-xs font-bold uppercase ${status === 'completed' ? 'text-green-600' : 'text-gray-500'}`}>Fertig</span>
                   </label>
                )}

                {/* --- NEU: ANZEIGE AKTIVER ÜBERSETZUNGS-AUFTRÄGE --- */}
                {internalMeeting?.translationJobs && Object.keys(internalMeeting.translationJobs).length > 0 && (
                    <div className="flex gap-2 mr-2">
                        {Object.entries(internalMeeting.translationJobs).map(([lang, status]) => (
                            <div key={lang} className={`flex items-center gap-1 px-2 py-1 rounded text-xs border ${status === 'completed' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700'} dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300`}>
                                <span className="uppercase font-bold">{lang}</span>
                                {status === 'pending' ? (
                                    // Spinner Animation
                                    <span className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full ml-1" title="In Bearbeitung..."></span>
                                ) : (
                                    <span className="ml-1" title="Fertig">✓</span>
                                )}
                                {/* Stornieren Button nur für Moderatoren bei Pending */}
                                {status === 'pending' && isModerator && (
                                    <button 
                                        onClick={(e) => { e.preventDefault(); handleCancelClick(lang); }} 
                                        className="ml-2 text-gray-400 hover:text-red-500 font-bold"
                                        title="Auftrag stornieren"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {/* ------------------------------------------------ */}

                <button onClick={(e) => { e.preventDefault(); handleOpenTranslateModal(null); }} className="text-gray-400 hover:text-blue-500 transition-colors" title="Übersetzen" disabled={!isModerator}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" /></svg>
                </button>
            </div>
        </div>

      </main>

      {/* === MOBILE BACKDROP (Für Right Sidebar) === */}
      {rightSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 z-30 lg:hidden backdrop-blur-sm transition-opacity" 
            onClick={() => setRightSidebarOpen(false)}
          />
      )}

      {/* === RECHTE SIDEBAR: KONTEXT (FIXED WIDTH, FULL HEIGHT SCROLL) === */}
      <aside 
        className={`
            fixed inset-y-0 right-0 z-40 bg-white dark:bg-gray-800 shadow-2xl transform transition-all duration-300 ease-in-out flex flex-col flex-shrink-0 h-full
            lg:relative lg:shadow-none lg:z-auto
            ${rightSidebarOpen ? 'translate-x-0 w-80' : 'translate-x-full lg:translate-x-0 w-0 lg:w-0 overflow-hidden'}
        `}
      >
        <div className={`p-4 border-b border-gray-200 dark:border-gray-700 font-bold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 whitespace-nowrap flex items-center justify-between flex-shrink-0 ${!rightSidebarOpen && 'hidden'}`}>
            <span>Meeting Kontext</span>
            <button onClick={() => setRightSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-gray-600">✕</button>
        </div>
        
        <div className={`flex-1 overflow-y-auto scrollbar-thin ${!rightSidebarOpen && 'hidden'}`}>
            {/* SECTION 1: ALLGEMEIN (Details) */}
            <AccordionSection title="Allgemein" isOpen={openSections.details} onToggle={() => toggleSection('details')} icon="📅">
                 <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Titel</label>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full p-2 border rounded text-sm font-medium bg-white dark:bg-gray-800 dark:border-gray-600" disabled={!isModerator}/>
                    </div>
                    <label className="block text-xs font-bold text-gray-400 uppercase">Datum</label>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full p-2 border rounded text-sm bg-white dark:bg-gray-800 dark:border-gray-600" disabled={!isModerator}/>
                    <div className="flex gap-2">
                        <div className="w-1/2"><label className="block text-xs font-bold text-gray-400 uppercase mb-1">Start</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full p-2 border rounded text-sm bg-white dark:bg-gray-800 dark:border-gray-600" disabled={!isModerator}/></div>
                        <div className="w-1/2"><label className="block text-xs font-bold text-gray-400 uppercase mb-1">Ende</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full p-2 border rounded text-sm bg-white dark:bg-gray-800 dark:border-gray-600" disabled={!isModerator}/></div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Ort</label>
                        <div className="flex items-center gap-2">
                            <div className="relative flex-grow"><input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full p-2 border rounded text-sm bg-white dark:bg-gray-800 dark:border-gray-600" disabled={isOnlineMeeting && !isModerator}/></div>
                            <label className="cursor-pointer" title="Online Meeting"><input type="checkbox" className="sr-only peer" checked={isOnlineMeeting} onChange={handleOnlineToggle} disabled={!isModerator}/><div className="w-8 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:bg-green-500 relative peer-checked:after:translate-x-full after:content-[''] after:absolute after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all after:shadow-sm"></div></label>
                        </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                        <select value={moderatorUid} onChange={(e) => setModeratorUid(e.target.value)} className="w-1/2 p-1 border rounded text-xs bg-white dark:bg-gray-800 dark:border-gray-600" disabled={!isModerator}>{internalParticipants.map(user => <option key={user.uid} value={user.uid}>{user.name}</option>)}</select>
                        <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="w-1/2 p-1 border rounded text-xs bg-white dark:bg-gray-800 dark:border-gray-600" disabled={!isModerator}><option value="internal">🔒 Intern</option><option value="public">🌍 Öffentlich</option><option value="secret">🕵️ Geheim</option></select>
                    </div>
                 </div>
            </AccordionSection>

            {/* SECTION 2: TEILNEHMER */}
            <AccordionSection title={`Teilnehmer (${allParticipants.length})`} isOpen={openSections.participants} onToggle={() => toggleSection('participants')} icon="👥">
                <div className="mb-3">
                    <ParticipantAutocomplete users={companyUsers} teams={teams} onSelect={handleAddParticipant} placeholder="Hinzufügen..." existingParticipants={allParticipants} isDisabled={!isModerator} />
                </div>
                <div className="space-y-4">
                     {presentParticipants.length > 0 && (<div className="space-y-1"><span className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Anwesend</span>{presentParticipants.map(p => (<div key={p.id} className="flex flex-col text-sm p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded group"><div className="flex justify-between items-center"><span className="truncate w-32 font-medium">{p.name || p.email}</span><div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">{!((p.events||[]).find(e=>e.manual&&e.type==='leave'))&&isModerator&&<button onClick={()=>handleSetTimeEvent(p.id,'early')} className="text-gray-400 hover:text-blue-500 text-xs" title="Früher gegangen">🕒</button>}<button onClick={()=>handleUpdateParticipantStatusLocal(p.id,'distribution',false)} disabled={!isModerator} className="text-gray-400 hover:text-blue-500 text-xs" title="Verteiler">✉️</button><button onClick={()=>handleUpdateParticipantStatusLocal(p.id,'absent',p.uid===currentUserId)} disabled={!isModerator&&p.uid!==currentUserId} className="text-red-400 hover:text-red-600 text-xs px-1" title="Abwesend">✕</button><button onClick={()=>handleRemovePerson(p.id)} disabled={!isModerator} className="text-gray-300 hover:text-red-500 text-xs ml-1" title="Entfernen">🗑</button></div></div>{((p.events||[]).find(e=>e.manual))&&<div className="flex gap-1 mt-0.5 text-[10px]">{(p.events||[]).find(e=>e.manual&&e.type==='join')&&<span className="bg-yellow-100 text-yellow-800 px-1 rounded">🕒 {(p.events||[]).find(e=>e.manual&&e.type==='join').label}</span>}{(p.events||[]).find(e=>e.manual&&e.type==='leave')&&<span className="bg-red-100 text-red-800 px-1 rounded">🚪 {(p.events||[]).find(e=>e.manual&&e.type==='leave').label}</span>}{isModerator&&<button onClick={()=>handleClearManualEvents(p.id)} className="hover:text-red-600 ml-0.5">×</button>}</div>}</div>))}</div>)}
                     
                     {pendingParticipants.length > 0 && (<div className="space-y-1"><span className="text-[10px] font-bold text-yellow-600 uppercase tracking-wider">Ausstehend</span>{pendingParticipants.map(p => (<div key={p.id} className="flex justify-between items-center text-sm p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded group"><span className="truncate w-32 text-gray-600 dark:text-gray-400">{p.name || p.email}</span><div className="flex gap-1"><button onClick={()=>handleUpdateParticipantStatusLocal(p.id,'present',p.uid===currentUserId)} disabled={!isModerator&&p.uid!==currentUserId} className="text-green-500 hover:text-green-700 text-xs">✓</button><button onClick={()=>handleRemovePerson(p.id)} disabled={!isModerator} className="text-gray-300 hover:text-red-500 text-xs" title="Entfernen">🗑</button></div></div>))}</div>)}
                     
                     {distributionParticipants.length > 0 && (<div className="space-y-1"><span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Verteiler</span>{distributionParticipants.map(p => (<div key={p.id} className="flex justify-between items-center text-sm p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"><span className="truncate w-32 text-gray-500 italic">{p.name || p.email}</span><button onClick={()=>handleRemovePerson(p.id)} disabled={!isModerator} className="text-gray-300 hover:text-red-500 text-xs" title="Entfernen">🗑</button></div>))}</div>)}
                     
                     {absentParticipants.length > 0 && (<div className="space-y-1"><span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">Abwesend</span>{absentParticipants.map(p => (<div key={p.id} className="flex justify-between items-center text-sm p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded group"><span className="truncate w-32 text-gray-400 line-through">{p.name || p.email}</span><div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">{isModerator&&<button onClick={()=>handleSetTimeEvent(p.id,'late')} className="text-gray-400 hover:text-blue-500 text-xs" title="Kam später">🕒</button>}<button onClick={()=>handleUpdateParticipantStatusLocal(p.id,'present',p.uid===currentUserId)} disabled={!isModerator&&p.uid!==currentUserId} className="text-green-500 hover:text-green-700 text-xs">✓</button><button onClick={()=>handleRemovePerson(p.id)} disabled={!isModerator} className="text-gray-300 hover:text-red-500 text-xs ml-1" title="Entfernen">🗑</button></div></div>))}</div>)}
                </div>
            </AccordionSection>

            {/* SECTION 3: VERKNÜPFUNGEN & THEMEN */}
            <AccordionSection title="Verknüpfungen" isOpen={openSections.resources} onToggle={() => toggleSection('resources')} icon="🔗">
                 <div className="space-y-4">
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded-lg border border-indigo-100 dark:border-indigo-800">
                        <ProjectAutocomplete 
                            companyId={currentCompanyId} 
                            userUid={currentUserId} 
                            onSelect={(p) => {
                                setProjectId(p.id);
                                setTopicSourceName(p.name);
                            }} 
                            initialValue={projectId} 
                            isDisabled={!isModerator} 
                            label="Projekt Verknüpfung" 
                            onNameResolved={setTopicSourceName}
                        />
                    </div>

                    {/* THEMENSPEICHER */}
                    {projectId && (
                        <div className="animate-fadeIn">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-gray-500 uppercase">Themenspeicher</span>
                                <span className="text-[10px] bg-gray-200 px-1.5 rounded text-gray-600">{displayedPool.length}</span>
                            </div>
                            <div 
                                onDragOver={handleDragOverPool}
                                onDrop={handleDropOnPool}
                                className={`min-h-[150px] max-h-[300px] overflow-y-auto rounded-lg border-2 border-dashed p-2 transition-colors flex flex-col gap-2 ${isDragOverPool ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-600'}`}
                            >
                                {displayedPool.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-400 text-center p-4">
                                        <span className="text-2xl mb-1">📭</span>
                                        <span className="text-xs italic">Alles erledigt.</span>
                                    </div>
                                ) : (
                                    displayedPool.map((proposal, index) => (
                                        <div 
                                            key={proposal.id}
                                            draggable={true}
                                            onDragStart={(e) => handleDragStart(e, 'pool', index, proposal)}
                                            className="bg-white dark:bg-gray-700 p-2 rounded shadow-sm border border-gray-200 dark:border-gray-600 hover:border-indigo-400 cursor-grab active:cursor-grabbing text-xs group relative"
                                        >
                                            {proposal.title && <p className="font-bold text-gray-900 dark:text-white mb-0.5 truncate">{proposal.title}</p>}
                                            <p className={`${proposal.title ? 'text-gray-600 dark:text-gray-300' : 'font-medium text-gray-800 dark:text-gray-200'} line-clamp-3 mb-1`}>{proposal.description}</p>
                                            <div className="flex justify-between items-center text-[9px] text-gray-400"><span className="truncate max-w-[80px]">{proposal.createdBy}</span></div>
                                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-indigo-400">⋮⋮</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                 </div>
            </AccordionSection>
        </div>
      </aside>

      {/* === MODALS === */}
      <AgendaProposalModal 
        isOpen={showProposalModal} 
        onClose={() => setShowProposalModal(false)} 
        onSubmit={handleProposalSubmit} 
      />
      
      <TranslationJobModal 
        isOpen={showTranslateModal} 
        onClose={() => setShowTranslateModal(false)} 
        onSubmit={handleTranslateSubmit}
        targetLanguage={targetLangForModal}
      />

      <GuestAssignmentModal 
        isOpen={showGuestModal} 
        onClose={() => setShowGuestModal(false)} 
        onSubmit={handleSaveGuests}
        currentGuests={currentGuestsForModal}
        companyId={currentCompanyId}
      />

    </div>
  );
});

export default MeetingForm;